import * as React from 'react'
import { FC, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import { TerritoryBoundary } from '@user/store/territory/typings'
import { TerritoryPreviewPanel } from './TerritoryPreviewPanel'
import { TerritoryOverlapWarning } from './TerritoryOverlapWarning'

const MAX_NAME_LENGTH = 100

type Props = {
  boundary: TerritoryBoundary
  /** The rule this territory is being drawn for, named so the trip has a visible purpose. */
  ruleName?: string
  onCancel: () => void
  onSave: (name: string) => void
}

/**
 * Names a territory drawn for an assignment rule (WWM-17508).
 *
 * A name and nothing else, on purpose. The rule the admin came from is about to ask for the
 * sales team, the owners and the funnel, and asking here as well would be asking twice for the
 * same decision -- so this collects the one thing the rule cannot supply and sends them back.
 *
 * Nothing is saved yet. The shape and name are parked, and the territory is created when the
 * rule is saved, because the server requires a sales team and that is the rule's to give. The
 * upshot is that abandoning the rule leaves no half-made territory behind.
 *
 * It still shows what the shape contains, which is the same panel the full create dialog uses:
 * the figures are the only way to tell a reasonable boundary from one that caught three houses.
 */
export const TerritoryNameDialog: FC<Props> = ({
  boundary,
  ruleName,
  onCancel,
  onSave,
}) => {
  const [name, setName] = useState('')

  const trimmed = name.trim()
  const tooLong = trimmed.length > MAX_NAME_LENGTH

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Name this territory</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {ruleName
            ? `This territory will be the condition for “${ruleName}”. You will choose the sales team and owners back on the rule.`
            : 'You will choose the sales team and owners back on the assignment rule.'}
        </DialogContentText>

        <TerritoryPreviewPanel boundary={boundary} />

        <TerritoryOverlapWarning boundary={boundary} />

        <TextField
          autoFocus
          fullWidth
          label="Territory name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={tooLong}
          helperText={
            tooLong ? `Keep it under ${MAX_NAME_LENGTH} characters.` : ' '
          }
          inputProps={{ 'data-test-id': 'territory-name-input' }}
          // Enter saves: this dialog has one field, and reaching for the mouse to confirm a
          // single word is friction a hundred territories would feel.
          onKeyDown={(e) => {
            if (e.key === 'Enter' && trimmed && !tooLong) onSave(trimmed)
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Discard</Button>
        <Button
          variant="contained"
          onClick={() => onSave(trimmed)}
          disabled={!trimmed || tooLong}
          data-test-id="territory-name-save"
        >
          Save and continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
